import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Check, CurrencyDollar, Sparkle, Star, Pencil, X,
  CircleNotch, Stack, Users, ChatCircleDots, Lightning, Headset,
} from "@phosphor-icons/react";

/* --- constants ---------------------------------------------------- */

const FEATURE_LIBRARY = [
  { id: "essential",        label: "Essencial (WhatsApp, CRM)" },
  { id: "decision_engine",  label: "Motor de Decisão" },
  { id: "campaigns",        label: "Campanhas em massa" },
  { id: "intelligence",     label: "Customer Intelligence" },
  { id: "roi_dashboard",    label: "Painel de ROI" },
  { id: "media_uploads",    label: "Upload de mídia" },
  { id: "customer_memory",  label: "Memória de cliente" },
  { id: "priority_support", label: "Suporte prioritário" },
  { id: "custom_integrations", label: "Integrações customizadas" },
  { id: "all",              label: "Todos os recursos" },
];

const LIMIT_ICONS = {
  max_active_customers: Users,
  monthly_messages: ChatCircleDots,
  monthly_premium_llm_calls: Sparkle,
  daily_help_assistant: Headset,
};

const LIMIT_LABELS = {
  max_active_customers: "Clientes ativos",
  monthly_messages: "Mensagens/mês",
  monthly_premium_llm_calls: "Chamadas LLM premium/mês",
  daily_help_assistant: "Help Assistant/dia",
};

/* --- helpers ------------------------------------------------------ */

const fmtBRL = (cents) => {
  if (!cents) return "Grátis";
  return `R$ ${(cents / 100).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  })}`;
};

const fmtLimit = (n) => (n === 0 || n == null) ? "Ilimitado" : n.toLocaleString("pt-BR");

const RECOMMENDED = "pro";

const PLAN_COLORS = {
  trial:      { ring: "ring-slate-600", gradient: "from-slate-500/10 to-slate-500/0", chip: "bg-slate-500/20 text-slate-300" },
  start:      { ring: "ring-sky-500",   gradient: "from-sky-500/10 to-sky-500/0",   chip: "bg-sky-500/20 text-sky-300" },
  pro:        { ring: "ring-amber-400", gradient: "from-amber-500/15 to-amber-500/0", chip: "bg-amber-400/20 text-amber-300" },
  enterprise: { ring: "ring-purple-500", gradient: "from-purple-500/15 to-purple-500/0", chip: "bg-purple-500/20 text-purple-300" },
};

/* --- main --------------------------------------------------------- */

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null); // plan object
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data);
  };

  useEffect(() => { load(); }, []);

  const mrrProjection = useMemo(() => {
    if (!plans) return 0;
    return plans.reduce((acc, p) => acc + (p.active ? p.price_cents : 0), 0);
  }, [plans]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        price_cents: Math.max(0, parseInt(editing.price_cents || 0, 10)),
        active: !!editing.active,
        limits: editing.limits,
        features: editing.features,
      };
      await api.put(`/admin/plans/${editing.code}`, payload);
      toast.success(`Plano ${payload.name} atualizado`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar plano");
    } finally {
      setSaving(false);
    }
  };

  if (plans === null) {
    return (
      <div className="px-4 md:px-8 py-14 md:py-16 text-center">
        <CircleNotch size={22} className="animate-spin text-slate-500 inline" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-7">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Plataforma</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">Planos comerciais</h1>
          <p className="text-sm text-slate-400 mt-1.5">
            {plans.length} planos · projeção por assinante ativo: <span className="text-white tabular-nums">{fmtBRL(mrrProjection)}</span>
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
        {plans.map((p) => (
          <PlanCard
            key={p.code}
            plan={p}
            recommended={p.code === RECOMMENDED}
            onEdit={() => setEditing(JSON.parse(JSON.stringify(p)))}
          />
        ))}
      </div>

      {/* Legend / info */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5 text-[12px] text-slate-400">
        <div className="flex items-center gap-2 text-white font-semibold text-[13px] mb-2">
          <Lightning size={14} weight="regular" /> Como os planos funcionam
        </div>
        <ul className="space-y-1.5">
          <li>• Alterações entram em vigor <strong className="text-slate-200">imediatamente</strong> para novos tenants. Tenants existentes mantêm os limites atuais até migração manual.</li>
          <li>• <strong className="text-slate-200">0</strong> em um limite significa <strong className="text-emerald-400">ilimitado</strong>. Use para planos Enterprise.</li>
          <li>• Planos <strong className="text-slate-200">inativos</strong> não contam no MRR mas continuam funcionando para clientes já vinculados.</li>
          <li>• Preço em <strong className="text-slate-200">centavos</strong> (ex: 99000 = R$ 990,00).</li>
        </ul>
      </div>

      {editing && (
        <PlanEditModal
          plan={editing}
          setPlan={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

/* --- sub-components ---------------------------------------------- */

function PlanCard({ plan, recommended, onEdit }) {
  const theme = PLAN_COLORS[plan.code] || PLAN_COLORS.start;
  const limits = plan.limits || {};
  const features = plan.features || [];

  return (
    <div
      data-testid={`plan-card-${plan.code}`}
      className={`relative bg-gradient-to-b ${theme.gradient} bg-[#0E0E11] border border-white/10 rounded-xl overflow-hidden transition-all hover:border-white/20 ${recommended ? `ring-1 ${theme.ring}` : ""}`}
    >
      {recommended && (
        <div className="absolute -top-px left-0 right-0 flex justify-center">
          <div className="flex items-center gap-1 bg-amber-400 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-b-md">
            <Star size={10} weight="fill" /> Recomendado
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-1">
          <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold px-2 py-0.5 rounded ${theme.chip}`}>
            {plan.code}
          </div>
          <button
            onClick={onEdit}
            data-testid={`plan-edit-${plan.code}`}
            className="text-slate-500 hover:text-white transition-colors p-1"
            aria-label="Editar plano"
          >
            <Pencil size={13} weight="regular" />
          </button>
        </div>

        <h2 className="font-heading text-xl font-semibold text-white mt-2">{plan.name}</h2>

        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-heading text-4xl font-semibold text-white tabular-nums tracking-tight">
            {fmtBRL(plan.price_cents)}
          </span>
          {plan.price_cents > 0 && <span className="text-slate-500 text-[12px]">/mês</span>}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
              plan.active
                ? "bg-emerald-400/15 text-emerald-400 border border-emerald-400/25"
                : "bg-slate-500/15 text-slate-400 border border-slate-500/25"
            }`}
          >
            <span className={`w-1 h-1 rounded-full ${plan.active ? "bg-emerald-400" : "bg-slate-500"}`} />
            {plan.active ? "Ativo" : "Inativo"}
          </span>
        </div>

        <div className="my-5 border-t border-white/5" />

        {/* Limits */}
        <div className="space-y-2 mb-5">
          {Object.keys(LIMIT_LABELS).map((k) => {
            const Icon = LIMIT_ICONS[k] || Stack;
            const v = limits[k];
            const isUnlimited = v === 0 || v == null;
            return (
              <div key={k} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Icon size={11} weight="regular" /> {LIMIT_LABELS[k]}
                </span>
                <span className={`text-[12px] tabular-nums ${isUnlimited ? "text-emerald-400" : "text-white"}`}>
                  {fmtLimit(v)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Features */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Inclui</div>
          <ul className="space-y-1.5">
            {features.length === 0 && (
              <li className="text-[12px] text-slate-600 italic">Sem recursos configurados</li>
            )}
            {features.map((f) => {
              const lib = FEATURE_LIBRARY.find((x) => x.id === f);
              return (
                <li key={f} className="flex items-start gap-2 text-[12px] text-slate-300">
                  <Check size={12} weight="bold" className="text-emerald-400 mt-[3px] shrink-0" />
                  <span>{lib?.label || f}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* --- Editor modal ------------------------------------------------- */

function PlanEditModal({ plan, setPlan, onCancel, onSave, saving }) {
  const setLimit = (k, v) => setPlan({ ...plan, limits: { ...plan.limits, [k]: v === "" ? 0 : parseInt(v, 10) || 0 } });
  const toggleFeature = (id) => {
    const has = plan.features.includes(id);
    setPlan({ ...plan, features: has ? plan.features.filter((f) => f !== id) : [...plan.features, id] });
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        data-testid="plan-edit-modal"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0E0E11] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#0E0E11] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Editar plano</div>
            <h3 className="font-heading text-lg font-semibold text-white mt-0.5">{plan.name}</h3>
          </div>
          <button
            onClick={onCancel}
            data-testid="plan-edit-close"
            className="text-slate-500 hover:text-white transition-colors p-1.5"
          >
            <X size={16} weight="regular" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-6">
          {/* Name + Price */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nome comercial">
              <input
                type="text"
                value={plan.name}
                onChange={(e) => setPlan({ ...plan, name: e.target.value })}
                data-testid="plan-name-input"
                className="input"
              />
            </Field>
            <Field label="Preço (em centavos)" helper={`${fmtBRL(parseInt(plan.price_cents || 0, 10))} / mês`}>
              <div className="relative">
                <CurrencyDollar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={plan.price_cents}
                  onChange={(e) => setPlan({ ...plan, price_cents: e.target.value })}
                  data-testid="plan-price-input"
                  className="input pl-8"
                />
              </div>
            </Field>
          </div>

          {/* Active toggle */}
          <Field label="Status do plano">
            <button
              onClick={() => setPlan({ ...plan, active: !plan.active })}
              data-testid="plan-active-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors ${plan.active ? "bg-emerald-500" : "bg-slate-700"}`}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                  plan.active ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
            <span className="ml-3 text-[12px] text-slate-400">
              {plan.active ? "Plano visível e disponível para contratação" : "Plano oculto — não aceita novos clientes"}
            </span>
          </Field>

          {/* Limits */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium mb-3">Limites</div>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(LIMIT_LABELS).map((k) => (
                <Field key={k} label={LIMIT_LABELS[k]} helper="0 = ilimitado">
                  <input
                    type="number"
                    min="0"
                    value={plan.limits?.[k] ?? 0}
                    onChange={(e) => setLimit(k, e.target.value)}
                    data-testid={`plan-limit-${k}`}
                    className="input"
                  />
                </Field>
              ))}
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium mb-3">Recursos inclusos</div>
            <div className="grid grid-cols-2 gap-2">
              {FEATURE_LIBRARY.map((f) => {
                const on = plan.features.includes(f.id);
                return (
                  <button
                    key={f.id}
                    onClick={() => toggleFeature(f.id)}
                    data-testid={`plan-feature-${f.id}`}
                    className={`text-left flex items-start gap-2 px-3 py-2 rounded-md border transition-colors ${
                      on
                        ? "border-emerald-400/40 bg-emerald-400/5 text-white"
                        : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-white/20 hover:text-slate-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded shrink-0 mt-0.5 flex items-center justify-center ${
                        on ? "bg-emerald-400" : "bg-white/5 border border-white/10"
                      }`}
                    >
                      {on && <Check size={10} weight="bold" className="text-slate-900" />}
                    </div>
                    <span className="text-[12px]">{f.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <footer className="sticky bottom-0 bg-[#0E0E11] border-t border-white/5 px-6 py-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="plan-edit-cancel"
            className="text-[12px] text-slate-400 hover:text-white px-3 py-2"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving || !plan.name.trim()}
            data-testid="plan-edit-save"
            className="inline-flex items-center gap-1.5 bg-white text-slate-900 hover:bg-slate-200 px-4 py-2 rounded-md text-[12px] font-semibold transition-colors disabled:opacity-60"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
            {saving ? "Salvando…" : "Salvar alterações"}
          </button>
        </footer>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          color: white;
          font-size: 13px;
          padding: 0.55rem 0.75rem;
          border-radius: 6px;
          outline: none;
          transition: border-color .15s;
        }
        .input:focus { border-color: rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}

function Field({ label, helper, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</span>
        {helper && <span className="text-[10px] text-slate-600">{helper}</span>}
      </div>
      {children}
    </label>
  );
}
